/**
 * Custom roles — owner-facing "Roles" management page.
 *
 * Route: `/settings/roles` (nested under SettingsPage). Owner-gated on the
 * `settings.manage_org` capability — both the sidebar link (SidebarTara's
 * `linkCapabilityGuards`) and this page render nothing actionable for anyone
 * else.
 *
 * One card per role (in the order the API returns). Each card edits the role's
 * name + description, a capability toggle matrix grouped by `group`, a
 * brand-access selector (`all` / `assigned`), and (Phase 2) which MCP
 * connectors the role may use (`all` vs an allowlist of platforms). Save
 * PATCHes only the changed fields.
 *
 * The OWNER role renders capabilities all-on + disabled with a note ("Owner
 * always has full access"), MCP access locked to "All connectors", and never
 * sends a `capabilities` patch — if the backend ever returns 422
 * `owner_role_locked` we surface a friendly message defensively.
 *
 * Phase 3 adds owner-driven lifecycle: a "New role" dialog (POST) and a delete
 * action on CUSTOM roles only (system roles never show delete). Delete surfaces
 * the `409 { assigned_employee_count }` "reassign first" conflict clearly.
 *
 * MUI throughout (matches the admin-portal / settings pages). Toasts go through
 * the shared Redux `setMessage` banner like the rest of the agency portal.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import store from '../../../store/store';
import { setMessage } from '../../../store/reducers/appSlice';
import { HOME_ROUTE } from '../../../lib/constants/route';
import { useCan } from '../../../lib/rbac';
import {
  AgencyRole,
  BrandAccess,
  CapabilityDef,
  CreateRoleBody,
  McpAccess,
  McpAccessMode,
  PlatformDef,
  UpdateRolePatch,
  createRole,
  deleteRole,
  listRoles,
  updateRole,
} from '../../../lib/api/roles/roles-api';

// ----------------------------------------------------------------------------
//  Constants & pure helpers
// ----------------------------------------------------------------------------

// The owner role is identified by its stable machine key. Its capabilities are
// always full and locked server-side; the UI mirrors that (all-on, disabled).
const OWNER_KEY = 'owner';

// Stable display order for capability groups. Any group not listed here is
// appended afterwards (alphabetically) so an unexpected backend group still
// renders rather than disappearing.
const GROUP_ORDER = [
  'Clients',
  'Proposals',
  'Team',
  'Skills',
  'Billing',
  'Notifications',
  'Stats',
  'Settings',
];

function groupCapabilities(
  caps: CapabilityDef[]
): Array<{ group: string; items: CapabilityDef[] }> {
  const byGroup = new Map<string, CapabilityDef[]>();
  for (const c of caps) {
    const g = c.group || 'Other';
    if (!byGroup.has(g)) byGroup.set(g, []);
    byGroup.get(g)!.push(c);
  }
  const known = GROUP_ORDER.filter((g) => byGroup.has(g));
  const extra = Array.from(byGroup.keys())
    .filter((g) => !GROUP_ORDER.includes(g))
    .sort();
  return [...known, ...extra].map((group) => ({
    group,
    items: byGroup.get(group)!,
  }));
}

// The API names capabilities and their groups in English. A language may
// translate them under these keys; one whose value is empty (English) or
// missing keeps the API's own text. (Dots would be read as nesting by i18next,
// hence the underscores.)
const capabilityKey = (key: string): string =>
  `RolesCapability_${key.replace(/\W+/g, '_')}`;
const capabilityGroupKey = (group: string): string =>
  `RolesGroup_${group.replace(/\W+/g, '_')}`;

// Two string arrays equal as sets (order-independent) — used to detect whether
// the capability selection actually changed before sending a patch.
function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((x) => setB.has(x));
}

// ---- MCP (connector) access helpers ----------------------------------------

// Normalise a possibly-absent `mcp_access` to a concrete value. A missing value
// (older backend) is treated as full access so nothing is silently restricted.
function normMcpAccess(m: McpAccess | undefined): {
  mode: McpAccessMode;
  platforms: Set<string>;
} {
  if (!m || m.mode === 'all') return { mode: 'all', platforms: new Set() };
  return { mode: 'allowlist', platforms: new Set(m.platforms ?? []) };
}

// Build the wire `mcp_access` value from the draft's mode + selected platforms.
function mcpAccessWire(mode: McpAccessMode, platforms: Set<string>): McpAccess {
  return mode === 'all'
    ? { mode: 'all' }
    : { mode: 'allowlist', platforms: Array.from(platforms) };
}

// True when two normalised MCP-access drafts are equivalent (mode + set).
function sameMcpAccess(
  a: { mode: McpAccessMode; platforms: Set<string> },
  b: { mode: McpAccessMode; platforms: Set<string> }
): boolean {
  if (a.mode !== b.mode) return false;
  if (a.mode === 'all') return true;
  return sameSet(Array.from(a.platforms), Array.from(b.platforms));
}

// ----------------------------------------------------------------------------
//  Per-role editable draft (local working copy)
// ----------------------------------------------------------------------------

interface RoleDraft {
  name: string;
  description: string;
  capabilities: Set<string>;
  brand_access: BrandAccess;
  mcpMode: McpAccessMode;
  mcpPlatforms: Set<string>;
}

function draftFromRole(role: AgencyRole): RoleDraft {
  const mcp = normMcpAccess(role.mcp_access);
  return {
    name: role.name ?? '',
    description: role.description ?? '',
    capabilities: new Set(role.capabilities ?? []),
    brand_access: role.brand_access ?? 'assigned',
    mcpMode: mcp.mode,
    mcpPlatforms: mcp.platforms,
  };
}

// Compute the minimal patch (only changed fields). Returns null when nothing
// changed. The owner's capabilities are NEVER included (locked server-side);
// the owner's mcp_access is forced to `all` server-side so we never send it.
function buildPatch(
  role: AgencyRole,
  draft: RoleDraft,
  isOwner: boolean
): UpdateRolePatch | null {
  const patch: UpdateRolePatch = {};
  const name = draft.name.trim();
  const description = draft.description.trim();

  if (name !== (role.name ?? '')) patch.name = name;
  if (description !== (role.description ?? '')) patch.description = description;
  if (draft.brand_access !== role.brand_access) {
    patch.brand_access = draft.brand_access;
  }
  if (!isOwner) {
    const draftCaps = Array.from(draft.capabilities);
    if (!sameSet(draftCaps, role.capabilities ?? [])) {
      patch.capabilities = draftCaps;
    }
    const draftMcp = { mode: draft.mcpMode, platforms: draft.mcpPlatforms };
    if (!sameMcpAccess(draftMcp, normMcpAccess(role.mcp_access))) {
      patch.mcp_access = mcpAccessWire(draft.mcpMode, draft.mcpPlatforms);
    }
  }

  return Object.keys(patch).length > 0 ? patch : null;
}

// ----------------------------------------------------------------------------
//  Shared editors (used by both the per-role card and the create dialog)
// ----------------------------------------------------------------------------

const showToast = (
  severity: 'success' | 'info' | 'warning' | 'error',
  message: string
) => store.dispatch(setMessage({ messageType: severity, message }));

interface CapabilityMatrixProps {
  grouped: Array<{ group: string; items: CapabilityDef[] }>;
  /** When true, every box is checked + disabled (owner). */
  allOn?: boolean;
  selected: Set<string>;
  disabled?: boolean;
  onToggle: (key: string, checked: boolean) => void;
}

/** Grouped capability checkbox grid — shared by the edit card + create dialog. */
function CapabilityMatrix({
  grouped,
  allOn = false,
  selected,
  disabled = false,
  onToggle,
}: CapabilityMatrixProps) {
  const { t } = useTranslation();
  if (grouped.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary'>
        {t('RolesNoCapabilities', 'No capabilities available.')}
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
        columnGap: 3,
        rowGap: 2,
      }}
    >
      {grouped.map(({ group, items }) => (
        <Box key={group}>
          <Typography
            variant='overline'
            color='text.secondary'
            sx={{ display: 'block', lineHeight: 1.6 }}
          >
            {t(capabilityGroupKey(group), '') || group}
          </Typography>
          <FormGroup>
            {items.map((cap) => {
              const checked = allOn || selected.has(cap.key);
              return (
                <FormControlLabel
                  key={cap.key}
                  control={
                    <Checkbox
                      size='small'
                      checked={checked}
                      disabled={allOn || disabled}
                      onChange={(e) => onToggle(cap.key, e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant='body2'>
                      {t(capabilityKey(cap.key), '') || cap.label}
                    </Typography>
                  }
                />
              );
            })}
          </FormGroup>
        </Box>
      ))}
    </Box>
  );
}

interface McpAccessSectionProps {
  allPlatforms: PlatformDef[];
  mode: McpAccessMode;
  platforms: Set<string>;
  /** Owner: locked to "All connectors", disabled, with an explanatory note. */
  locked?: boolean;
  disabled?: boolean;
  onModeChange: (mode: McpAccessMode) => void;
  onTogglePlatform: (key: string, checked: boolean) => void;
}

/**
 * Per-role MCP (connector) access editor: a toggle between "All connectors" and
 * "Selected connectors", and — when on the allowlist — a checkbox list of every
 * platform from `all_platforms`. Owner is rendered locked to "All connectors".
 */
function McpAccessSection({
  allPlatforms,
  mode,
  platforms,
  locked = false,
  disabled = false,
  onModeChange,
  onTogglePlatform,
}: McpAccessSectionProps) {
  const { t } = useTranslation();
  const effectiveMode: McpAccessMode = locked ? 'all' : mode;
  return (
    <Box>
      <Typography variant='subtitle2' gutterBottom>
        {t('RolesMcpAccess', 'MCP connector access')}
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
        {t(
          'RolesMcpAccessHint',
          'Which connected platforms (Meta, Google Ads, GA4, …) members of this role can use through the assistant.'
        )}
      </Typography>

      {locked && (
        <Alert severity='info' sx={{ mb: 1.5 }}>
          {t(
            'RolesOwnerAllConnectors',
            'The Owner role can always use every connector.'
          )}
        </Alert>
      )}

      <ToggleButtonGroup
        value={effectiveMode}
        exclusive
        size='small'
        color='primary'
        disabled={locked || disabled}
        onChange={(_, val: McpAccessMode | null) => {
          if (val) onModeChange(val);
        }}
      >
        <ToggleButton value='all'>
          {t('RolesAllConnectors', 'All connectors')}
        </ToggleButton>
        <ToggleButton value='allowlist'>
          {t('RolesSelectedConnectors', 'Selected connectors')}
        </ToggleButton>
      </ToggleButtonGroup>

      {effectiveMode === 'allowlist' && !locked && (
        <Box sx={{ mt: 1.5 }}>
          {allPlatforms.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              {t('RolesNoConnectors', 'No connectors available.')}
            </Typography>
          ) : (
            <FormGroup>
              {allPlatforms.map((p) => (
                <FormControlLabel
                  key={p.key}
                  control={
                    <Checkbox
                      size='small'
                      checked={platforms.has(p.key)}
                      disabled={disabled}
                      onChange={(e) => onTogglePlatform(p.key, e.target.checked)}
                    />
                  }
                  label={<Typography variant='body2'>{p.label}</Typography>}
                />
              ))}
            </FormGroup>
          )}
        </Box>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------------------
//  Single role card
// ----------------------------------------------------------------------------

interface RoleCardProps {
  role: AgencyRole;
  allCapabilities: CapabilityDef[];
  allPlatforms: PlatformDef[];
  onSaved: (updated: AgencyRole) => void;
  onRequestDelete: (role: AgencyRole) => void;
}

function RoleCard({
  role,
  allCapabilities,
  allPlatforms,
  onSaved,
  onRequestDelete,
}: RoleCardProps) {
  const { t } = useTranslation();
  const isOwner = role.key === OWNER_KEY;
  const [draft, setDraft] = useState<RoleDraft>(() => draftFromRole(role));
  const [saving, setSaving] = useState(false);

  // Re-seed the draft if the underlying role changes identity (e.g. after a
  // successful save replaces it, or a fresh list load). Keyed on id so in-flight
  // edits to the SAME role aren't clobbered on every parent render.
  useEffect(() => {
    setDraft(draftFromRole(role));
  }, [role]);

  const grouped = useMemo(
    () => groupCapabilities(allCapabilities),
    [allCapabilities]
  );

  const patch = useMemo(
    () => buildPatch(role, draft, isOwner),
    [role, draft, isOwner]
  );
  const isDirty = patch !== null;
  const nameInvalid = draft.name.trim().length === 0;

  const toggleCap = (key: string, checked: boolean) => {
    if (isOwner) return; // owner is locked all-on
    setDraft((d) => {
      const next = new Set(d.capabilities);
      if (checked) next.add(key);
      else next.delete(key);
      return { ...d, capabilities: next };
    });
  };

  const setMcpMode = (mode: McpAccessMode) =>
    setDraft((d) => ({ ...d, mcpMode: mode }));

  const toggleMcpPlatform = (key: string, checked: boolean) =>
    setDraft((d) => {
      const next = new Set(d.mcpPlatforms);
      if (checked) next.add(key);
      else next.delete(key);
      return { ...d, mcpPlatforms: next };
    });

  const handleSave = useCallback(async () => {
    if (!patch || nameInvalid || saving) return;
    setSaving(true);
    try {
      const updated = await updateRole(role.id, patch);
      onSaved(updated);
      showToast(
        'success',
        t('RolesSaved', '“{{role}}” saved.', {
          role: updated.name || role.name,
        })
      );
    } catch (err) {
      const e = err as {
        status?: number;
        code?: string;
        message?: string;
        reason?: string;
      };
      if (e?.code === 'owner_role_locked' || e?.status === 422) {
        if (e?.code === 'owner_role_locked') {
          showToast(
            'info',
            t(
              'RolesOwnerLocked',
              'The Owner role always has full access and cannot be changed.'
            )
          );
        } else {
          showToast(
            'error',
            e?.message || t('RolesChangesInvalid', 'Some changes were invalid.')
          );
        }
      } else if (e?.status === 403) {
        showToast(
          'error',
          e?.reason ||
            t(
              'RolesNoPermissionEdit',
              'You do not have permission to edit this role.'
            )
        );
      } else {
        showToast(
          'error',
          e?.message || t('RolesSaveFailed', 'Failed to save role.')
        );
      }
    } finally {
      setSaving(false);
    }
  }, [patch, nameInvalid, saving, role.id, role.name, onSaved, t]);

  const handleReset = () => setDraft(draftFromRole(role));

  return (
    <Card variant='outlined'>
      <CardContent>
        {/* ---- Header: name + system chip ---- */}
        <Stack
          direction='row'
          alignItems='center'
          spacing={1}
          sx={{ mb: 0.5 }}
          flexWrap='wrap'
          rowGap={1}
        >
          <Typography variant='h6' component='h2'>
            {role.name}
          </Typography>
          {role.is_system && (
            <Tooltip
              title={t('RolesSystemTooltip', 'Built-in role provisioned by ADUP')}
            >
              <Chip
                label={t('RolesSystem', 'System')}
                size='small'
                color='default'
              />
            </Tooltip>
          )}
          {isOwner && (
            <Chip
              label={t('RolesFullAccess', 'Full access')}
              size='small'
              color='primary'
            />
          )}
          {!role.is_assignable && (
            <Chip
              label={t('RolesNotAssignable', 'Not assignable')}
              size='small'
              variant='outlined'
              color='warning'
            />
          )}
        </Stack>

        {isOwner && (
          <Alert severity='info' sx={{ mb: 2 }}>
            {t(
              'RolesOwnerNote',
              "The Owner role always has full access. Its capabilities can't be edited."
            )}
          </Alert>
        )}

        {/* ---- Editable name + description ---- */}
        <Stack spacing={2} sx={{ mb: 2 }}>
          <TextField
            label={t('RolesRoleName', 'Role name')}
            value={draft.name}
            onChange={(e) =>
              setDraft((d) => ({ ...d, name: e.target.value }))
            }
            size='small'
            fullWidth
            required
            error={nameInvalid}
            helperText={
              nameInvalid ? t('RolesNameRequired', 'Name is required.') : undefined
            }
            disabled={saving}
          />
          <TextField
            label={t('RolesDescription', 'Description')}
            value={draft.description}
            onChange={(e) =>
              setDraft((d) => ({ ...d, description: e.target.value }))
            }
            size='small'
            fullWidth
            multiline
            minRows={2}
            disabled={saving}
          />
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* ---- Brand access ---- */}
        <Box sx={{ mb: 2 }}>
          <Typography variant='subtitle2' gutterBottom>
            {t('RolesBrandAccess', 'Brand access')}
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
            {t(
              'RolesBrandAccessHint',
              'Whether members of this role act on all brands or only the brands assigned to them.'
            )}
          </Typography>
          <ToggleButtonGroup
            value={draft.brand_access}
            exclusive
            size='small'
            color='primary'
            disabled={isOwner || saving}
            onChange={(_, val: BrandAccess | null) => {
              if (val) setDraft((d) => ({ ...d, brand_access: val }));
            }}
          >
            <ToggleButton value='all'>
              {t('RolesAllBrands', 'All brands')}
            </ToggleButton>
            <ToggleButton value='assigned'>
              {t('RolesAssignedOnly', 'Assigned only')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* ---- MCP connector access ---- */}
        <McpAccessSection
          allPlatforms={allPlatforms}
          mode={draft.mcpMode}
          platforms={draft.mcpPlatforms}
          locked={isOwner}
          disabled={saving}
          onModeChange={setMcpMode}
          onTogglePlatform={toggleMcpPlatform}
        />

        <Divider sx={{ my: 2 }} />

        {/* ---- Capability matrix (grouped) ---- */}
        <Typography variant='subtitle2' gutterBottom>
          {t('RolesCapabilities', 'Capabilities')}
        </Typography>
        <CapabilityMatrix
          grouped={grouped}
          allOn={isOwner}
          selected={draft.capabilities}
          disabled={saving}
          onToggle={toggleCap}
        />
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2, justifyContent: 'space-between' }}>
        {/* Delete is only ever available on CUSTOM roles. */}
        <Box>
          {!role.is_system && (
            <Button
              size='small'
              variant='text'
              color='error'
              onClick={() => onRequestDelete(role)}
              disabled={saving}
            >
              {t('RolesDeleteRole', 'Delete role')}
            </Button>
          )}
        </Box>
        <Stack direction='row' spacing={1}>
          <Button
            size='small'
            variant='text'
            onClick={handleReset}
            disabled={!isDirty || saving}
          >
            {t('RolesReset', 'Reset')}
          </Button>
          <Button
            size='small'
            variant='contained'
            onClick={handleSave}
            disabled={!isDirty || nameInvalid || saving}
            startIcon={
              saving ? (
                <CircularProgress size={14} color='inherit' />
              ) : undefined
            }
          >
            {saving
              ? t('RolesSaving', 'Saving…')
              : t('RolesSaveChanges', 'Save changes')}
          </Button>
        </Stack>
      </CardActions>
    </Card>
  );
}

// ----------------------------------------------------------------------------
//  Create-role dialog
// ----------------------------------------------------------------------------

interface CreateRoleDialogProps {
  open: boolean;
  allCapabilities: CapabilityDef[];
  allPlatforms: PlatformDef[];
  onClose: () => void;
  onCreated: (role: AgencyRole) => void;
}

function CreateRoleDialog({
  open,
  allCapabilities,
  allPlatforms,
  onClose,
  onCreated,
}: CreateRoleDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [capabilities, setCapabilities] = useState<Set<string>>(new Set());
  const [brandAccess, setBrandAccess] = useState<BrandAccess>('assigned');
  const [mcpMode, setMcpMode] = useState<McpAccessMode>('all');
  const [mcpPlatforms, setMcpPlatforms] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  // Reset the form every time the dialog (re)opens.
  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
      setCapabilities(new Set());
      setBrandAccess('assigned');
      setMcpMode('all');
      setMcpPlatforms(new Set());
      setSubmitting(false);
    }
  }, [open]);

  const grouped = useMemo(
    () => groupCapabilities(allCapabilities),
    [allCapabilities]
  );
  const nameInvalid = name.trim().length === 0;

  const toggleCap = (key: string, checked: boolean) =>
    setCapabilities((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });

  const toggleMcpPlatform = (key: string, checked: boolean) =>
    setMcpPlatforms((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });

  const handleCreate = async () => {
    if (nameInvalid || submitting) return;
    setSubmitting(true);
    const body: CreateRoleBody = {
      name: name.trim(),
      description: description.trim() || undefined,
      capabilities: Array.from(capabilities),
      brand_access: brandAccess,
      mcp_access: mcpAccessWire(mcpMode, mcpPlatforms),
    };
    try {
      const created = await createRole(body);
      showToast(
        'success',
        t('RolesCreated', '“{{role}}” created.', { role: created.name })
      );
      onCreated(created);
    } catch (err) {
      const e = err as { status?: number; message?: string; reason?: string };
      if (e?.status === 422) {
        showToast(
          'error',
          e?.message ||
            t(
              'RolesDetailsInvalid',
              'Some details were invalid. Check the name and try again.'
            )
        );
      } else if (e?.status === 403) {
        showToast(
          'error',
          e?.reason ||
            t(
              'RolesNoPermissionCreate',
              'You do not have permission to create roles.'
            )
        );
      } else {
        showToast(
          'error',
          e?.message || t('RolesCreateFailed', 'Failed to create role.')
        );
      }
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth='md' fullWidth>
      <DialogTitle>{t('RolesNewRole', 'New role')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mb: 2 }}>
          <TextField
            label={t('RolesRoleName', 'Role name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            size='small'
            fullWidth
            required
            autoFocus
            error={nameInvalid && name.length > 0}
            helperText={
              nameInvalid && name.length > 0
                ? t('RolesNameRequired', 'Name is required.')
                : undefined
            }
            disabled={submitting}
          />
          <TextField
            label={t('RolesDescription', 'Description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size='small'
            fullWidth
            multiline
            minRows={2}
            disabled={submitting}
          />
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant='subtitle2' gutterBottom>
            {t('RolesBrandAccess', 'Brand access')}
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
            {t(
              'RolesBrandAccessHint',
              'Whether members of this role act on all brands or only the brands assigned to them.'
            )}
          </Typography>
          <ToggleButtonGroup
            value={brandAccess}
            exclusive
            size='small'
            color='primary'
            disabled={submitting}
            onChange={(_, val: BrandAccess | null) => {
              if (val) setBrandAccess(val);
            }}
          >
            <ToggleButton value='all'>
              {t('RolesAllBrands', 'All brands')}
            </ToggleButton>
            <ToggleButton value='assigned'>
              {t('RolesAssignedOnly', 'Assigned only')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Divider sx={{ my: 2 }} />

        <McpAccessSection
          allPlatforms={allPlatforms}
          mode={mcpMode}
          platforms={mcpPlatforms}
          disabled={submitting}
          onModeChange={setMcpMode}
          onTogglePlatform={toggleMcpPlatform}
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant='subtitle2' gutterBottom>
          {t('RolesCapabilities', 'Capabilities')}
        </Typography>
        <CapabilityMatrix
          grouped={grouped}
          selected={capabilities}
          disabled={submitting}
          onToggle={toggleCap}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          {t('Cancel', 'Cancel')}
        </Button>
        <Button
          variant='contained'
          onClick={handleCreate}
          disabled={nameInvalid || submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={14} color='inherit' />
            ) : undefined
          }
        >
          {submitting
            ? t('RolesCreating', 'Creating…')
            : t('RolesCreateRole', 'Create role')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ----------------------------------------------------------------------------
//  Delete-role confirmation dialog
// ----------------------------------------------------------------------------

interface DeleteRoleDialogProps {
  role: AgencyRole | null;
  onClose: () => void;
  onDeleted: (role: AgencyRole) => void;
}

function DeleteRoleDialog({ role, onClose, onDeleted }: DeleteRoleDialogProps) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  // Set when the backend returns 409: N employees still hold this role.
  const [blockedCount, setBlockedCount] = useState<number | null>(null);

  // Clear transient state whenever the target role changes.
  useEffect(() => {
    setSubmitting(false);
    setBlockedCount(null);
  }, [role]);

  const handleDelete = async () => {
    if (!role || submitting) return;
    setSubmitting(true);
    setBlockedCount(null);
    try {
      await deleteRole(role.id);
      showToast(
        'success',
        t('RolesDeleted', '“{{role}}” deleted.', { role: role.name })
      );
      onDeleted(role);
    } catch (err) {
      const e = err as {
        status?: number;
        message?: string;
        reason?: string;
        assigned_employee_count?: number;
      };
      if (e?.status === 409) {
        // Reassign-first conflict — keep the dialog open and explain.
        setBlockedCount(
          typeof e.assigned_employee_count === 'number'
            ? e.assigned_employee_count
            : 0
        );
      } else if (e?.status === 403) {
        showToast(
          'error',
          e?.reason ||
            t('RolesSystemNoDelete', 'System roles cannot be deleted.')
        );
        onClose();
      } else {
        showToast(
          'error',
          e?.message || t('RolesDeleteFailed', 'Failed to delete role.')
        );
        onClose();
      }
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={!!role}
      onClose={submitting ? undefined : onClose}
      maxWidth='xs'
      fullWidth
    >
      <DialogTitle>{t('RolesDeleteRole', 'Delete role')}</DialogTitle>
      <DialogContent>
        {blockedCount !== null ? (
          <Alert severity='warning'>
            {blockedCount === 1
              ? t(
                  'RolesBlockedOne',
                  '1 employee still has this role — reassign them first.'
                )
              : t(
                  'RolesBlockedMany',
                  '{{employees}} employees still have this role — reassign them first.',
                  { employees: blockedCount }
                )}
          </Alert>
        ) : (
          <Typography variant='body2'>
            {t('RolesDeleteConfirmLead', 'Delete the role')}{' '}
            <strong>“{role?.name}”</strong>
            {t('RolesDeleteConfirmTail', "? This can't be undone.")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          {blockedCount !== null ? t('Close', 'Close') : t('Cancel', 'Cancel')}
        </Button>
        {blockedCount === null && (
          <Button
            variant='contained'
            color='error'
            onClick={handleDelete}
            disabled={submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={14} color='inherit' />
              ) : undefined
            }
          >
            {submitting
              ? t('RolesDeleting', 'Deleting…')
              : t('Delete', 'Delete')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ----------------------------------------------------------------------------
//  Page
// ----------------------------------------------------------------------------

const SettingsRoles = () => {
  const { t } = useTranslation();
  const canManageOrg = useCan('settings.manage_org');

  const [roles, setRoles] = useState<AgencyRole[]>([]);
  const [allCapabilities, setAllCapabilities] = useState<CapabilityDef[]>([]);
  const [allPlatforms, setAllPlatforms] = useState<PlatformDef[]>([]);
  const [status, setStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const [error, setError] = useState<string | null>(null);

  // Phase 3 — create / delete dialog state.
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AgencyRole | null>(null);

  const load = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      const res = await listRoles();
      setRoles(res.data);
      setAllCapabilities(res.all_capabilities);
      setAllPlatforms(res.all_platforms);
      setStatus('success');
    } catch (err) {
      const e = err as { status?: number; message?: string };
      // eslint-disable-next-line no-console
      console.error('[SettingsRoles] load failed', err);
      if (e?.status === 403) {
        setError(
          t(
            'RolesNoPermissionManage',
            'You do not have permission to manage roles.'
          )
        );
      } else {
        setError(e?.message || t('RolesLoadFailed', 'Could not load roles.'));
      }
      setStatus('error');
    }
    // `t` is left out: a language switch must not re-fetch the roles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (canManageOrg) load();
  }, [canManageOrg, load]);

  // Replace a single role in place after a save (preserves list order).
  const handleSaved = useCallback((updated: AgencyRole) => {
    setRoles((prev) =>
      prev.map((r) => (r.id === updated.id ? updated : r))
    );
  }, []);

  // After a successful create: close the dialog and refetch so the new role (and
  // the freshly-provisioned key/order from the backend) appears.
  const handleCreated = useCallback(() => {
    setCreateOpen(false);
    load();
  }, [load]);

  // After a successful delete: close the dialog and refetch.
  const handleDeleted = useCallback(() => {
    setDeleteTarget(null);
    load();
  }, [load]);

  // ---- Owner gate ----
  if (!canManageOrg) {
    return (
      <SubPageInnerContentHolder title={t('Roles')} backBtnLink={HOME_ROUTE}>
        <Alert severity='warning'>
          {t(
            'RolesNoPermissionAsk',
            "You don't have permission to manage roles. Ask an owner for access."
          )}
        </Alert>
      </SubPageInnerContentHolder>
    );
  }

  return (
    <SubPageInnerContentHolder title={t('Roles')} backBtnLink={HOME_ROUTE}>
      <Box>
        <Stack
          direction='row'
          alignItems='flex-start'
          justifyContent='space-between'
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Typography variant='body2' color='text.secondary' sx={{ maxWidth: 560 }}>
            {t(
              'RolesIntro',
              'Define what each role can do across the agency, which brands its members reach, and which connectors they can use. Capability changes take effect for members the next time they load the app.'
            )}
          </Typography>
          <Button
            variant='contained'
            size='small'
            onClick={() => setCreateOpen(true)}
            disabled={status === 'loading'}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            {t('RolesNewRole', 'New role')}
          </Button>
        </Stack>

        {status === 'error' && (
          <Alert
            severity='error'
            sx={{ mb: 2 }}
            action={
              <Button color='inherit' size='small' onClick={load}>
                {t('Retry', 'Retry')}
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {status === 'loading' && (
          <Stack
            direction='row'
            alignItems='center'
            spacing={2}
            sx={{ py: 4 }}
          >
            <CircularProgress size={22} />
            <Typography>{t('RolesLoading', 'Loading roles…')}</Typography>
          </Stack>
        )}

        {status === 'success' && roles.length === 0 && (
          <Alert severity='info'>
            {t('RolesNoneFound', 'No roles found for this organisation.')}
          </Alert>
        )}

        {status === 'success' && roles.length > 0 && (
          <Stack spacing={3}>
            {roles.map((role) => (
              <RoleCard
                key={role.id}
                role={role}
                allCapabilities={allCapabilities}
                allPlatforms={allPlatforms}
                onSaved={handleSaved}
                onRequestDelete={setDeleteTarget}
              />
            ))}
          </Stack>
        )}
      </Box>

      <CreateRoleDialog
        open={createOpen}
        allCapabilities={allCapabilities}
        allPlatforms={allPlatforms}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />

      <DeleteRoleDialog
        role={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={handleDeleted}
      />
    </SubPageInnerContentHolder>
  );
};

export default SettingsRoles;
